import React, { useEffect, useRef } from 'react';
import { formatTime } from './utils';

const CalendarWeekView = ({ currentDate, meetings, onDateClick, onEventClick, onEventHover }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    // Delay slightly to ensure the browser has fully rendered the scroll container heights
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        // Auto-scroll to 10:00 AM (10 * hourHeight)
        scrollRef.current.scrollTo({ top: 10 * 64, behavior: 'smooth' });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [currentDate]);

  const isSameDay = (d1, d2) => 
    d1?.getDate() === d2?.getDate() && 
    d1?.getMonth() === d2?.getMonth() && 
    d1?.getFullYear() === d2?.getFullYear();

  // Get start of week (Sunday)
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  const startOfWeek = getStartOfWeek(currentDate);
  
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    return d;
  });

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const hourHeight = 64; // 64px per hour

  const getEventStyle = (meeting) => {
    const start = new Date(meeting.meetingStart);
    const end = new Date(meeting.meetingEnd);
    
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();
    const durationMinutes = Math.max(endMinutes - startMinutes, 15);

    const top = (startMinutes / 60) * hourHeight;
    const height = (durationMinutes / 60) * hourHeight;

    return {
      top: `${top}px`,
      height: `${height}px`,
    };
  };

  const handleTimeSlotClick = (date, hour, e) => {
    if (e.target.dataset.timeslot) {
      const clickedDate = new Date(date);
      clickedDate.setHours(hour, 0, 0, 0);
      onDateClick(clickedDate);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[600px] overflow-hidden">
      {/* Header (Days of week) */}
      <div className="flex-none flex border-b border-slate-200 bg-slate-50 pr-2"> {/* pr-2 accounts for the vertical scrollbar width */}
        <div className="w-16 flex-none border-r border-transparent"></div> {/* Spacer to align with time labels */}
        <div className="flex-1 flex">
          {weekDays.map((date, i) => {
            const isToday = isSameDay(date, new Date());
            return (
              <div key={i} className="flex-1 p-2 text-center border-r last:border-r-0 border-slate-200">
                <div className="text-xs font-semibold text-slate-500 uppercase">
                  {date.toLocaleDateString('default', { weekday: 'short' })}
                </div>
                <div className={`text-lg font-bold mt-1 w-8 h-8 mx-auto flex items-center justify-center rounded-full
                  ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-slate-800'}
                `}>
                  {date.getDate()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Scrollable Time Grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative custom-scrollbar">
        <div className="flex min-w-[700px]">
          {/* Time Labels */}
          <div className="w-16 flex-none border-r border-slate-100 bg-white">
            {hours.map(hour => (
              <div 
                key={`label-${hour}`} 
                className={`text-[11px] text-slate-400 font-medium text-right pr-2 relative ${hour === 0 ? 'top-1' : '-top-2'}`}
                style={{ height: `${hourHeight}px` }}
              >
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
            ))}
          </div>

          {/* 7-Day Grid */}
          <div className="flex-1 flex relative">
            {weekDays.map((date, dayIndex) => {
              const dayMeetings = meetings.filter(m => isSameDay(new Date(m.meetingStart), date));
              
              return (
                <div key={dayIndex} className="flex-1 border-r last:border-r-0 border-slate-100 relative">
                  {/* Grid Lines */}
                  {hours.map(hour => (
                    <div 
                      key={`grid-${hour}`} 
                      data-timeslot="true"
                      onClick={(e) => handleTimeSlotClick(date, hour, e)}
                      className="border-b border-slate-200 w-full cursor-pointer hover:bg-slate-100 transition-colors relative group bg-white"
                      style={{ height: `${hourHeight}px` }}
                    >
                      {/* 30-minute dashed line */}
                      <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-slate-100 pointer-events-none group-hover:border-slate-200 transition-colors" />
                    </div>
                  ))}
                  
                  {/* Events */}
                  {dayMeetings.map(m => (
                    <div
                      key={m.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick(m, e); }}
                      className="absolute left-1 right-1 bg-blue-100/70 hover:bg-blue-100 border border-blue-200 border-l-[3px] border-l-blue-600 text-blue-900 p-1.5 rounded-r-md rounded-l-sm cursor-pointer transition-colors overflow-hidden z-10"
                      style={getEventStyle(m)}
                    >
                      <div className="text-[10px] font-bold truncate leading-tight">{m.title}</div>
                      <div className="text-[9px] text-blue-700/80 font-medium truncate mt-0.5">
                        {formatTime(m.meetingStart)} - {formatTime(m.meetingEnd)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWeekView;
